import { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ForgotPasswordModal } from '../../components/auth/ForgotPasswordModal';
import type { UserRole } from '../../types/user';

// Apagado por defecto: no confirmado todavía si las 4 cuentas demo existen
// en Identity Platform con password real — ver docs/frontend/PENDIENTES.md
// bajo FE-01. Cuando se confirme, VITE_ENABLE_DEMO_LOGIN=true las muestra.
const DEMO_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_DEMO_LOGIN === 'true';

const DEMO_ACCOUNTS: { role: UserRole; email: string; label: string; color: string }[] = [
  { role: 'supervisor', email: 'supervisor@demo.com', label: 'Supervisor', color: '#3B82F6' },
  { role: 'executive', email: 'ejecutivo@demo.com', label: 'Ejecutivo', color: '#8B5CF6' },
  { role: 'admin', email: 'admin@demo.com', label: 'Administrador', color: '#F59E0B' },
  { role: 'operator', email: 'operador@demo.com', label: 'Operador', color: '#10B981' },
];

export default function LoginPage() {
  const { login, loginDev, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);

  // Sin navigate() explícito: cuando isAuthenticated pasa a true, AppRouter
  // reevalúa la ruta /login y la reemplaza por <RoleRedirect />, que ya
  // conoce el rol real del usuario recién autenticado.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
    } catch {
      setError('Correo o contraseña incorrectos.');
    }
  };

  // En DEV entra directo con loginDev (sin tocar Firebase, ver AuthContext).
  // Fuera de DEV, loginDev no existe: solo precarga el correo, porque no se
  // conoce ninguna contraseña real de demo todavía.
  const handleDemoClick = (account: (typeof DEMO_ACCOUNTS)[number]) => {
    setError('');
    if (loginDev) {
      loginDev(account.role);
      return;
    }
    setEmail(account.email);
  };

  return (
    <div className="login-page">
      {/* Background effects */}
      <div className="login-page__bg">
        <div className="login-page__bg-orb login-page__bg-orb--1" />
        <div className="login-page__bg-orb login-page__bg-orb--2" />
        <div className="login-page__bg-grid" />
      </div>

      {/* Theme Switcher Button */}
      <button
        id="login-theme-btn"
        className="login-page__theme-btn"
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      >
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      <div className="login-page__card">
        {/* Logo */}
        <div className="login-page__logo">
          <img
            src="/assets/paitron_logo.png"
            alt="Paitron Radar"
            className="login-page__logo-img"
            onError={e => {
              const img = e.target as HTMLImageElement;
              img.style.display = 'none';
              const fallback = img.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <div style={{ display: 'none' }}>
            <h1 className="login-page__logo-name">Paitron Radar</h1>
            <p className="login-page__logo-tag">Supervisión Inteligente de Flotas</p>
          </div>
        </div>

        <h2 className="login-page__title">Iniciar sesión</h2>
        <p className="login-page__subtitle">Accede a tu plataforma de gestión de flota</p>

        <form onSubmit={handleSubmit} className="login-page__form" id="login-form">
          <Input
            id="login-email"
            label="Correo electrónico"
            type="email"
            placeholder="tu@empresa.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            leftIcon={<Mail size={16} />}
            required
            autoComplete="email"
          />
          <Input
            id="login-password"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            leftIcon={<Lock size={16} />}
            required
            autoComplete="current-password"
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              type="button"
              className="login-page__show-pass"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              {showPassword ? 'Ocultar' : 'Mostrar'} contraseña
            </button>
            <button
              type="button"
              id="forgot-password-btn"
              className="login-page__forgot-link"
              onClick={() => setForgotOpen(true)}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {error && <div className="login-page__error" role="alert">{error}</div>}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isLoading}
            className="login-page__submit"
            id="login-submit-btn"
          >
            Ingresar
          </Button>
        </form>

        {/* Demo accounts — solo precarga el correo, ver DEMO_LOGIN_ENABLED */}
        {DEMO_LOGIN_ENABLED && (
          <div className="login-page__demo">
            <p className="login-page__demo-title">Acceso rápido (demo)</p>
            <div className="login-page__demo-grid">
              {DEMO_ACCOUNTS.map(account => (
                <button
                  key={account.role}
                  id={`demo-login-${account.role}`}
                  className="login-page__demo-btn"
                  style={{ '--demo-color': account.color } as React.CSSProperties}
                  onClick={() => handleDemoClick(account)}
                  disabled={isLoading}
                >
                  {account.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal isOpen={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
